import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed, DestroyRef,
    inject,
    input,
    output,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormsModule,
    ReactiveFormsModule, Validators,
} from '@angular/forms';
import { FormBuilderSelectAcModule } from '@monsido/angular-shared-components';
import { TranslateModule } from '@client/app/modules/translate.module';
import { Domain } from '@monsido/modules/models/api/domain';

import {
    cmsHasOptionalURL,
    cmsHasPath,
    cmsHasSubDomain,
    cmsHasURL, getCmsOptions,
    isCmsRequiresSubDomain, resolveCmsURLField,
} from '@client/app/core/constants/domain-cms-config';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { ComponentsModule } from '@client/app/components/components.module';
import { MON_CMS_OPTIONS } from '@client/app/core/constants/helpers/monsido-cms-options';
import { DefaultConstraintsAndExcludesService } from '../../default-constraints-and-excludes-service/default-constraints-and-excludes.service';

type CmsSelectorOption = {
    name: string,
    id: string,
};

type CmsExtraFormControlBaseOptions = {
    name: string;
    label: string;
    required: boolean;
    placeholder: string;
    tooltip: string;
};
type CmsExtraFormControlInputOptions = CmsExtraFormControlBaseOptions & {
    controlType?: 'input'
    defaultValue?: string
    helpTxt?: string
};
type CmsExtraFormControlSelectOptions = CmsExtraFormControlBaseOptions & {
    controlType: 'select'
    selectOptions: { value: string, name: string }[]
    defaultValue: string
    helpTxt?: string
};

type CmsExtraFormControlOptions = CmsExtraFormControlInputOptions | CmsExtraFormControlSelectOptions;

class CmsExtraFormControl {
    readonly controlType: 'input' | 'select' = 'input';
    constructor (
        readonly options: CmsExtraFormControlOptions,
    ) {
        if (options.controlType) {
            this.controlType = options.controlType;
        }
    }
}

@Component({
    selector: 'form-domain-cms',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        FormBuilderSelectAcModule,
        TranslateModule,
        NgbTooltip,
        ComponentsModule,
    ],
    templateUrl: './form-domain-detail-cms.component.html',
})
export class FormDomainDetailCmsComponent {
    domain = input<Domain>();
    isCmsTemplate = computed(() => {
        return this.domain()?.cms === 'cms_template';
    });
    validityChange = output<boolean>();

    formBuilder = inject(FormBuilder);
    translateService = inject(TranslateService);
    cdRef = inject(ChangeDetectorRef);
    destroyRef = inject(DestroyRef);
    defaultConstraintsAndExcludesService = inject(DefaultConstraintsAndExcludesService);

    cmsOptions: CmsSelectorOption[] = [];
    cmsExtraFormControls: CmsExtraFormControl[] = [];
    cmsForm = this.formBuilder.group({
        cms: new FormControl(''),
        cmsExtra: this.formBuilder.group({}),
    });

    ngOnInit (): void {
        this.cmsOptions = getCmsOptions(this.isCmsTemplate());
        const selectedCms = this.domain()?.cms;
        if (selectedCms) {
            this.defaultConstraintsAndExcludesService.updateCurrentCms(selectedCms);
            this.cmsForm.patchValue({ cms: selectedCms });
        }
        this.renderForm(selectedCms || null, () => {
            const selectedCmsExtra = this.domain()?.cms_extra;
            if (selectedCmsExtra) {
                this.cmsForm.patchValue({ cmsExtra: selectedCmsExtra });
            }
        });

        const cmsFormStatus$ = this.cmsForm.statusChanges.subscribe(() => {
            this.validityChange.emit(this.cmsForm.valid || this.cmsForm.pristine);
        });

        const cmsControlValue$ = this.cmsForm.get('cms')?.valueChanges.subscribe(cms => {
            this.renderForm(cms);
        });

        const cmsFormValue$ = this.cmsForm.valueChanges.subscribe((value) => {
            const domain = this.domain();
            if (!domain) {
                return;
            }
            const { cms, cmsExtra } = value;
            if (!cms) {
                domain.cms = '';
                domain.cms_extra = {};
                return;
            }

            domain.cms = cms;
            domain.cms_extra = cmsExtra || {};
            this.defaultConstraintsAndExcludesService.updateCurrentCms(cms);
        });

        this.destroyRef.onDestroy(() => {
            cmsFormStatus$.unsubscribe();
            cmsControlValue$?.unsubscribe();
            cmsFormValue$.unsubscribe();
        });
    }

    private renderForm (cms: string | null, cb?: () => void) {
        // Reset form controls
        this.cmsExtraFormControls = [];
        for (const control of Object.keys(this.cmsForm.controls.cmsExtra.controls)) {
            this.cmsForm.controls.cmsExtra.removeControl(control);
        }

        if (!cms) {
            return;
        }

        const cmsExtraFormControls: CmsExtraFormControl[] = [];
        if (cmsHasSubDomain(cms)) {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'subdomain',
                    required: isCmsRequiresSubDomain(cms),
                    label: this.translateService.getString('Subdomain'),
                    tooltip: this.translateService.getString('The subdomain url to the CMS page'),
                    placeholder: this.translateService.getString('Subdomain url'),
                }),
            );
        }
        if (cmsHasPath(cms)) {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'cms_path',
                    required: true,
                    label: this.translateService.getString('CMS Path'),
                    tooltip: 'https://domain.tld/#{cms_path}/entries/#{section.value}/#{id.value}',
                    placeholder: this.translateService.getString('CMS Path'),
                }),
            );
        }
        if (cmsHasURL(cms)) {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: resolveCmsURLField(cms),
                    required: true,
                    label: this.translateService.getString('CMS url'),
                    tooltip: this.translateService.getString('The base url to the CMS page'),
                    placeholder: this.translateService.getString('CMS url'),
                }),
            );
        }
        if (cmsHasOptionalURL(cms)) {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'base_url',
                    required: false,
                    label: this.translateService.getString('CMS url'),
                    tooltip: this.translateService.getString('The optional base url to the CMS page'),
                    placeholder: this.translateService.getString('CMS url'),
                }),
            );
        }
        if (cms === 'episerver') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'episerver_path',
                    required: false,
                    label: this.translateService.getString('EPI Server Path'),
                    tooltip: this.translateService.getString('The optional epi server path'),
                    placeholder: this.translateService.getString('EPIServer'),
                }),
            );
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'contentid_tag',
                    required: false,
                    label: this.translateService.getString('Content id tag'),
                    tooltip: this.translateService.getString('Ex: data-currentpage, or data-pageid, or CurrentPageLink'),
                    placeholder: '',
                }),
            );
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'language_tag',
                    required: false,
                    label: this.translateService.getString('Language tag'),
                    tooltip: this.translateService.getString('Ex: lang or CurrentPageLanguage'),
                    placeholder: '',
                }),
            );
        }
        if (cms === 'konform') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'language_id',
                    required: false,
                    label: this.translateService.getString('Language id'),
                    tooltip: '',
                    placeholder: '',
                }),
                new CmsExtraFormControl({
                    name: 'areadynamicpage',
                    required: false,
                    label: this.translateService.getString('Area dynamic page'),
                    tooltip: '',
                    placeholder: '',
                }),
                new CmsExtraFormControl({
                    name: 'newspage',
                    required: false,
                    label: this.translateService.getString('Newspage'),
                    tooltip: '',
                    placeholder: '',
                }),
                new CmsExtraFormControl({
                    name: 'employeepage',
                    required: false,
                    label: this.translateService.getString('Employeepage'),
                    tooltip: '',
                    placeholder: '',
                }),
            );
        }
        if (cms === 'special') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'special_key',
                    required: true,
                    label: this.translateService.getString('Meta name'),
                    tooltip: this.translateService.getString('The name of a meta entry (page metadata)'),
                    placeholder: this.translateService.getString('Meta name'),
                }),
                new CmsExtraFormControl({
                    name: 'url',
                    required: true,
                    label: this.translateService.getString('URL template'),
                    tooltip: '',
                    placeholder: 'https://domain.name/path/<%= key %>',
                }),
            );
        }
        if (cms === 'wordpress_multisite') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'prefix_path',
                    required: true,
                    label: this.translateService.getString('Prefix path'),
                    tooltip: this.translateService.getString('The subpath/subfolder the WordPress installation is under'),
                    placeholder: this.translateService.getString('Prefix path'),
                }),
            );
        }
        if (cms === 'umbraco') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'path_template',
                    required: true,
                    label: this.translateService.getString('Path template'),
                    tooltip: this.translateService.getString('The Path template for Umbraco CMS'),
                    placeholder: '/umbraco/umbraco.aspx?app=content&rightAction=editContent&id=<%= id %>#content',
                }),
            );
        }
        if (cms === 'squiz') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    controlType: 'select',
                    selectOptions: [
                        {
                            value: MON_CMS_OPTIONS.ADMIN,
                            name: this.translateService.getString('Admin'),
                        },
                        {
                            value: MON_CMS_OPTIONS.EDITOR,
                            name: this.translateService.getString('Editor'),
                        },
                    ],
                    name: 'url_postfix',
                    required: true,
                    label: this.translateService.getString('Mode'),
                    tooltip: '',
                    placeholder: '',
                    defaultValue: MON_CMS_OPTIONS.EDITOR,
                }),
            );
        }
        if (cms === 'joomla') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    controlType: 'select',
                    selectOptions: [
                        {
                            value: MON_CMS_OPTIONS.ORIGINAL,
                            name: this.translateService.getString('Original'),
                        },
                        {
                            value: MON_CMS_OPTIONS.ALTERNATIVE,
                            name: this.translateService.getString('Alternative'),
                        },
                    ],
                    name: 'url_structure',
                    required: true,
                    label: this.translateService.getString('URL Structure'),
                    tooltip: '',
                    placeholder: '',
                    defaultValue: MON_CMS_OPTIONS.ORIGINAL,
                }),
            );
        }
        if (cms === 'plone') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    controlType: 'select',
                    selectOptions: [
                        {
                            value: MON_CMS_OPTIONS.STANDARD,
                            name: this.translateService.getString('Standard'),
                        },
                        {
                            value: MON_CMS_OPTIONS.ALTERNATIVE,
                            name: this.translateService.getString('Alternative'),
                        },
                    ],
                    name: 'version',
                    required: true,
                    label: this.translateService.getString('URL format'),
                    tooltip: '',
                    placeholder: '',
                    helpTxt: this.translateService.getString('Standard for http(s)://edit.domain.tld/inz/<path>. Alternative for http(s)://domain.tld/<path>/edit.'),
                    defaultValue: MON_CMS_OPTIONS.STANDARD,
                }),
            );
        }
        if (cms === 'aem') {
            cmsExtraFormControls.push(
                new CmsExtraFormControl({
                    name: 'default_language',
                    required: false,
                    label: this.translateService.getString('Default language'),
                    tooltip: '',
                    placeholder: '',
                }),
            );
        }

        for (const cmsExtraFormControl of cmsExtraFormControls) {
            const { options } = cmsExtraFormControl;
            this.cmsForm.controls.cmsExtra.addControl(
                options.name,
                new FormControl(options.defaultValue || '', options.required ? Validators.required : null),
            );
        }

        // Render form controls in the next cycle
        setTimeout(() => {
            this.cmsExtraFormControls = cmsExtraFormControls;
            if (cb) {
                cb();
            }
            this.cdRef.markForCheck();
        });
    }
}
